import React, { useRef } from 'react';
import Markdown from 'markdown-to-jsx';
import Button, { LoadingButton } from '@atlaskit/button';
import { Checkbox } from '@atlaskit/checkbox';
import Heading from '@atlaskit/heading';
import CheckIcon from '@atlaskit/icon/glyph/check-circle';
import { Box, Inline, Stack, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import { TransitionHomeContentBox } from '../../../../common/ui/transition-home/content-box';
import taskMessages from '../../../../common/utils/tasks/messages';
import { useTransitioningStatus } from '../../../../services/store/transitioning-status';
import { useDeactivateOG } from '../../../../services/use-deactivate-og';
import messages from './messages';

const Layout = ({
	children,
	heading,
}: {
	children: React.ReactNode;
	heading?: React.ReactNode;
}) => (
	<TransitionHomeContentBox illustration={<></>}>
		<Stack space="space.200" alignInline="start">
			<Heading as="h2" size="small">
				{heading ?? <FormattedMessage {...taskMessages.notificationsTaskThreeNonFinal} />}
			</Heading>
			{children}
		</Stack>
	</TransitionHomeContentBox>
);

export const CardTwo = () => {
	const [{ isRBNEnabled, ogAppExistsAtTransitioningStart, areOgAppsDeactivated }] =
		useTransitioningStatus();

	const { deactivate, loading } = useDeactivateOG();

	const { formatMessage } = useIntl();

	const checkbox = useRef<HTMLInputElement>(null);
	const handleDeactivate = () => {
		checkbox.current?.reportValidity();
		if (!checkbox.current?.validity.valid) return;
		deactivate();
	};

	// #region ogAppExistsAtTransitioningStart === false

	if (!ogAppExistsAtTransitioningStart && !isRBNEnabled) {
		throw new Error('CardTwo - unexpected state');
	}

	// 2D
	if (!ogAppExistsAtTransitioningStart && isRBNEnabled) {
		return (
			<Layout heading={<FormattedMessage {...taskMessages.contactMethodTitleNonFinal} />}>
				<Box>
					<Markdown>{formatMessage(messages.contactMethodDescriptionNonFinal)}</Markdown>
				</Box>
				<Button href="/jira/settings/personal/alert-notifications" target="_blank">
					{formatMessage(messages.manageContactMethodsNonFinal)}
				</Button>
			</Layout>
		);
	}

	// #endregion

	// #region ogAppExistsAtTransitioningStart === true

	// 2A & 2B
	if (ogAppExistsAtTransitioningStart && !isRBNEnabled) {
		return (
			<Layout>
				<Box>
					<Markdown>{formatMessage(messages.avoidDuplicateNotificationsNonFinal)}</Markdown>
				</Box>
				{!areOgAppsDeactivated && (
					<Checkbox
						ref={checkbox}
						isRequired
						label={formatMessage(messages.acknowledgeTurnOffOgNonFinal)}
					/>
				)}
				{!areOgAppsDeactivated && (
					<LoadingButton onClick={handleDeactivate} isLoading={loading}>
						{formatMessage(messages.turnOffOgCtaNonFinal)}
					</LoadingButton>
				)}
				{/* TODO: baseline alignment does not work with this icon? */}
				{areOgAppsDeactivated && (
					<Inline xcss={inlineStyles} alignBlock="baseline" space="space.050">
						<CheckIcon label="" primaryColor={token('color.icon', colors.N500)} size="small" />
						<FormattedMessage {...messages.ogTurnedOffNonFinal} />
					</Inline>
				)}
			</Layout>
		);
	}

	// 2C
	if (ogAppExistsAtTransitioningStart && isRBNEnabled && areOgAppsDeactivated) {
		return (
			<>
				<Layout heading={<FormattedMessage {...taskMessages.preventPushTitleNonFinal} />}>
					<Box>
						<Markdown>{formatMessage(messages.preventPushDescriptionNonFinal)}</Markdown>
					</Box>
					<Inline xcss={inlineStyles} alignBlock="baseline" space="space.050">
						<CheckIcon label="" primaryColor={token('color.icon', colors.N500)} size="small" />
						<FormattedMessage {...messages.ogTurnedOffNonFinal} />
					</Inline>
				</Layout>

				<Layout
					heading={<FormattedMessage {...taskMessages.deleteOgContactMethodsTitleNonFinal} />}
				>
					<Box>
						<Markdown>{formatMessage(messages.deleteOgCantactMethodsDescriptionNonFinal)}</Markdown>
					</Box>
					<Button href="/jira/settings/personal/alert-notifications" target="_blank">
						{formatMessage(messages.manageContactMethodsNonFinal)}
					</Button>
				</Layout>
			</>
		);
	}

	// 2E
	if (ogAppExistsAtTransitioningStart && isRBNEnabled && !areOgAppsDeactivated) {
		return (
			<Layout heading={<FormattedMessage {...taskMessages.preventPushTitleNonFinal} />}>
				<Box>
					<Markdown>{formatMessage(messages.preventPushDescriptionNonFinal)}</Markdown>
				</Box>
				<Checkbox
					ref={checkbox}
					isRequired
					label={formatMessage(messages.acknowledgeTurnOffOgNonFinal)}
				/>
				<LoadingButton onClick={handleDeactivate} isLoading={loading}>
					{formatMessage(messages.turnOffOgCtaNonFinal)}
				</LoadingButton>
			</Layout>
		);
	}
	// #endregion

	throw new Error('CardOne - unexpected state');
};

const inlineStyles = xcss({
	color: 'color.text.subtlest',
});
